import React, { useEffect, useState } from "react";

import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionToggleEventHandler,
  Button,
  Text,
} from "@fluentui/react-components";
import { SaveIcon } from "@fluentui/react-icons-mdl2";
import { devices } from "library/constants";
import { IAttachment, IEmployee, IFormattedFile } from "library/types";
import { genUUID } from "library/utils";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AppLogoSpinner } from "components/AppLogoSpinner/AppLogoSpinner";
import { Flex } from "components/styled";
import { UploadFile } from "components/UploadFile/UploadFile";
import { useAppTheme } from "hooks/useAppTheme";

import { AttachmentHeader } from "./AttachmentHeader";
import { AttachmentItem } from "./AttachmentItem";

type Props = {
  item: IEmployee;

  attachmentActionsState: {
    createAttachmentInProgress: boolean;
    deleteInProgress: boolean;
    onCreateAttachment: (attach: IFormattedFile | null, isHiddenForEmployee?: boolean) => void;
    onDeleteAttachment: (attach: IAttachment) => void;
  };
};

export function ManageEmployeeDocuments({ item, attachmentActionsState }: Props) {
  const { t } = useTranslation();
  const theme = useAppTheme();

  const [openAttachForms, setOpenAttachForms] = React.useState([""]);
  const handleToggleAttachForm: AccordionToggleEventHandler<string> = (event, data) => {
    setOpenAttachForms(data.openItems);
  };

  const [attachmentList, setAttachmentList] = React.useState<IAttachment[]>([]);
  const [newAttach, setNewAttach] = useState<IFormattedFile | null>(null);
  const [isAttachHiddenForEmployee, setIsAttachHiddenForEmployee] = useState(true);

  useEffect(() => {
    setAttachmentList(item.attachments || []);
  }, [item?.attachments]);

  const { deleteInProgress, createAttachmentInProgress, onCreateAttachment } =
    attachmentActionsState;

  return (
    <>
      <Container>
        {deleteInProgress || createAttachmentInProgress ? <AppLogoSpinner /> : null}
        <AttachmentList>
          <AddNewAttachment>
            <UploadFile
              defaultValue={newAttach}
              setFieldValue={(_, file) => {
                setNewAttach(file);
              }}
              fieldName=""
              label=""
            />
            {newAttach && (
              <Flex wrap="nowrap">
                <Button
                  style={{
                    padding: "5px 15px",
                  }}
                  icon={
                    <SaveIcon
                      style={{
                        color: theme.palette.themePrimary,
                        opacity: newAttach ? "1" : "0.5",
                      }}
                    />
                  }
                  // disabled={updateInProgress}
                  appearance="outline"
                  size="small"
                  onClick={async (e) => {
                    e.stopPropagation();
                    await onCreateAttachment(newAttach, isAttachHiddenForEmployee);
                    setNewAttach(null);
                    setIsAttachHiddenForEmployee(true);
                  }}
                />
              </Flex>
            )}
          </AddNewAttachment>
          <Row className="row-item">
            <Text weight="semibold">{t("hbh.name.label")}</Text>
            <Text weight="semibold">{t("hbh.uploaded.label")}</Text>
            <Text weight="semibold" align="end" style={{ paddingRight: "5px" }}>
              Action
            </Text>
          </Row>
          <Accordion
            openItems={openAttachForms}
            onToggle={handleToggleAttachForm}
            multiple
            collapsible
          >
            {attachmentList?.map((attachment, index) => {
              return (
                <>
                  <AccordionItem key={genUUID() + "" + index} value={attachment?.attachmentName}>
                    <AttachmentHeader
                      attachment={attachment}
                      openAttachForms={openAttachForms}
                      attachmentActionsState={attachmentActionsState}
                    />
                    <AccordionPanel>
                      <AttachmentItem
                        attachment={attachment}
                        key={attachment.attachmentName + "_" + index}
                      />
                    </AccordionPanel>
                  </AccordionItem>
                </>
              );
            })}
          </Accordion>
        </AttachmentList>
      </Container>
    </>
  );
}
const Container = styled.div`
  .fui-AccordionHeader__button {
    padding: 0;
  }
`;
const AttachmentList = styled.div`
  display: flex;
  flex-direction: column;
  //   border: 1px solid ${({ theme }) => theme.palette.neutralLight};
  margin: 10px 0;
  max-width: 800px;
`;

const AddNewAttachment = styled.div`
  display: flex;
  gap: 20px;
  padding: 5px 15px;
  margin-bottom: 20px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr 1fr;
  width: 100%;
  max-width: 800px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.neutralLight};
  padding: 10px;
  align-items: center;
  box-sizing: border-box;
  @media only screen and ${devices.md} {
    grid-template-columns: 6fr 1fr 1fr;
  }
`;
